import {
  DEFAULT_PLANNING_LEVELS,
  LEVEL_DESCRIPTIONS,
  LEVEL_LABELS,
  type DefaultPlanningLevel,
} from '@church/permissions'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useApi } from '../api-context.tsx'
import { sessionKeys } from '../session/keys.ts'
import { COMMON_STOREFRONTS } from '@church/domain'
import { servicesKeys } from './useServices.ts'
import { songCatalogKeys } from './useSongCatalog.ts'

/**
 * ForServices' church settings (docs/14): the Planning & people level every
 * person has by default — Scheduled viewer unless a church-wide ForServices
 * Administrator changes it. A floor, never a cap: a grant or role above it
 * still wins. Only church-wide Administrators read or change it.
 */
export function useServicesSettings({ enabled = true }: { enabled?: boolean } = {}) {
  const api = useApi()
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: servicesKeys.settings(),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.services.settings.get()
      if (error) throw error
      return data
    },
  })
  const update = useMutation({
    mutationFn: async (patch: {
      defaultPlanningLevel?: DefaultPlanningLevel
      appleMusicStorefront?: string
    }) => {
      const { data, error } = await api.api.v1.services.settings.patch(patch)
      if (error) throw error
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(servicesKeys.settings(), data)
      // Their own access rests on it too (it is everyone's floor).
      void queryClient.invalidateQueries({ queryKey: sessionKeys.me() })
      // Songs are matched again in a new storefront.
      void queryClient.invalidateQueries({ queryKey: songCatalogKeys.all() })
    },
  })
  return {
    ...query,
    update,
    /** The levels a church may give everyone, lowest first, each with what it allows. */
    choices: DEFAULT_PLANNING_LEVELS.map((level) => ({
      level,
      label: LEVEL_LABELS[level],
      description: LEVEL_DESCRIPTIONS.planning[level],
    })),
    /** Apple Music storefronts to offer, the church's own first if it is not a common one. */
    storefronts: (current: string) =>
      [...new Set([current, ...COMMON_STOREFRONTS])].map((code) => ({
        code,
        label: countryName(code),
      })),
  }
}

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(undefined, { type: 'region' })
  } catch {
    return null
  }
})()

/** "United Kingdom" for "gb" (the code itself where the platform has no names). */
export function countryName(code: string): string {
  try {
    return regionNames?.of(code.toUpperCase()) ?? code.toUpperCase()
  } catch {
    return code.toUpperCase()
  }
}
